import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const LegalFooter: React.FC = () => {
  const location = useLocation();
  const [leftOffset, setLeftOffset] = useState(0);

  const shouldOffsetForSidebar = useMemo(() => {
    const path = location.pathname || '';
    return !['/', '/authentication-portal'].includes(path);
  }, [location.pathname]);

  useEffect(() => {
    if (!shouldOffsetForSidebar) {
      setLeftOffset(0);
      return;
    }

    const sidebar = document.getElementById('dashboard-sidebar');
    if (!sidebar) {
      setLeftOffset(0);
      return;
    }

    const update = () => {
      const w = Math.round(sidebar.getBoundingClientRect().width || 0);
      setLeftOffset(window.innerWidth >= 1024 ? w : 0);
    };

    update();

    const onResize = () => update();
    window.addEventListener('resize', onResize);

    const ro = new ResizeObserver(() => update());
    ro.observe(sidebar);

    return () => {
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [shouldOffsetForSidebar, location.pathname]);

  return (
    <footer className="mt-10 w-full border-t border-border bg-gradient-to-b from-card to-background text-foreground">
      <div
        className="px-4 py-8 md:px-6 md:py-10 transition-[padding] duration-300"
        style={leftOffset ? { paddingLeft: leftOffset + 24 } : undefined}
      >
        <div className="mx-auto max-w-screen-2xl grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <div className="h-14 w-14 rounded-lg flex items-center justify-center">
                <img src="/assets/scola-logo.png" alt="Scola" className="w-12 h-12 object-contain" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-lg font-extrabold tracking-tight bg-gradient-to-r from-blue-700 via-sky-500 to-green-500 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Sora, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}
                >
                  Scola
                </div>
                <div className="text-xs text-muted-foreground leading-tight">Platformă educațională pentru școli</div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Scola. Toate drepturile rezervate.
            </div>
          </div>

          {/* Desktop columns */}
          <div className="hidden sm:block space-y-3">
            <div className="text-sm font-semibold">Documente legale</div>
            <nav className="grid grid-cols-1 gap-2 text-sm">
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/regulament-platforma" state={{ backgroundLocation: location }}>
                Regulament platformă
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/abonamente" state={{ backgroundLocation: location }}>
                Abonamente & implementare
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/dpa" state={{ backgroundLocation: location }}>
                Acord împuternicit (DPA)
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/termeni-si-conditii" state={{ backgroundLocation: location }}>
                Termeni și condiții
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/politica-de-confidentialitate" state={{ backgroundLocation: location }}>
                Politica de confidențialitate
              </Link>
            </nav>
          </div>

          <div className="hidden sm:block space-y-3">
            <div className="text-sm font-semibold">Informații</div>
            <nav className="grid grid-cols-1 gap-2 text-sm">
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/politica-de-cookies" state={{ backgroundLocation: location }}>
                Politica de cookies
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/gdpr" state={{ backgroundLocation: location }}>
                GDPR
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/contact" state={{ backgroundLocation: location }}>
                Contact
              </Link>
              <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/about" state={{ backgroundLocation: location }}>
                Despre noi
              </Link>
            </nav>
          </div>

          <div className="hidden sm:block space-y-3">
            <div className="text-sm font-semibold">Linkuri utile</div>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://anpc.ro/" target="_blank" rel="noreferrer noopener">
                ANPC
              </a>
              <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer noopener">
                SOL/ODR (UE)
              </a>
              <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://www.dataprotection.ro/" target="_blank" rel="noreferrer noopener">
                ANSPDCP
              </a>
            </div>
          </div>

          {/* Mobile accordion */}
          <div className="sm:hidden space-y-3">
            <details className="border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer list-none select-none px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <Icon name="Menu" size={16} className="text-muted-foreground" />
                  <span>Documente legale</span>
                </div>
                <Icon name="ChevronDown" size={18} className="text-muted-foreground" />
              </summary>
              <div className="px-4 pb-4 pt-1">
                <nav className="grid grid-cols-1 gap-2 text-sm text-center">
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/regulament-platforma" state={{ backgroundLocation: location }}>
                    Regulament platformă
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/abonamente" state={{ backgroundLocation: location }}>
                    Abonamente & implementare
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/dpa" state={{ backgroundLocation: location }}>
                    Acord împuternicit (DPA)
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/termeni-si-conditii" state={{ backgroundLocation: location }}>
                    Termeni și condiții
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/politica-de-confidentialitate" state={{ backgroundLocation: location }}>
                    Politica de confidențialitate
                  </Link>
                </nav>
              </div>
            </details>

            <details className="border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer list-none select-none px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <Icon name="Menu" size={16} className="text-muted-foreground" />
                  <span>Informații</span>
                </div>
                <Icon name="ChevronDown" size={18} className="text-muted-foreground" />
              </summary>
              <div className="px-4 pb-4 pt-1">
                <nav className="grid grid-cols-1 gap-2 text-sm text-center">
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/politica-de-cookies" state={{ backgroundLocation: location }}>
                    Politica de cookies
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/legal/gdpr" state={{ backgroundLocation: location }}>
                    GDPR
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/contact" state={{ backgroundLocation: location }}>
                    Contact
                  </Link>
                  <Link className="text-muted-foreground hover:text-foreground hover:underline" to="/about" state={{ backgroundLocation: location }}>
                    Despre noi
                  </Link>
                </nav>
              </div>
            </details>

            <details className="border border-border rounded-lg bg-card overflow-hidden">
              <summary className="cursor-pointer list-none select-none px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <Icon name="Menu" size={16} className="text-muted-foreground" />
                  <span>Linkuri utile</span>
                </div>
                <Icon name="ChevronDown" size={18} className="text-muted-foreground" />
              </summary>
              <div className="px-4 pb-4 pt-1">
                <div className="grid grid-cols-1 gap-2 text-sm text-center">
                  <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://anpc.ro/" target="_blank" rel="noreferrer noopener">
                    ANPC
                  </a>
                  <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer noopener">
                    SOL/ODR (UE)
                  </a>
                  <a className="text-muted-foreground hover:text-foreground hover:underline" href="https://www.dataprotection.ro/" target="_blank" rel="noreferrer noopener">
                    ANSPDCP
                  </a>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default LegalFooter;
